<script setup>
import { ref, computed, watch } from 'vue';
import { Button, Spin, DatePicker } from 'ant-design-vue';
import { ReloadOutlined } from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import { taipeiDay } from '@/utils/taipeiTime';
import { getConsultationBookingAvailability } from '@/api/consultation/consultationApi';
import { formatSlotTimeLabel, SLOT_STARTS } from '@/constants/consultantScheduleSlots';

const props = defineProps({
    consultantCoreMemberId: { type: String, default: '' },
    mode: { type: String, default: 'online' },
    modelValue: { type: String, default: null },
    /** 編輯時保留目前時段（即使 booking 標為已滿） */
    preserveSlotStartsAt: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    /** 變更時強制重拉時段（例如開啟編輯 modal） */
    reloadToken: { type: [Number, String], default: 0 }
});

const emit = defineEmits(['update:modelValue']);

const selectedMonth = ref(dayjs().format('YYYY-MM'));
const loading = ref(false);
const error = ref('');
const days = ref([]);
const selectedDate = ref('');

/** 正規化成 API 用的 YYYY-MM（避免 DatePicker 回傳顯示字串） */
const normalizeMonth = (raw) => {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'string' && /^\d{4}-\d{2}$/.test(raw.trim())) {
        return raw.trim();
    }
    if (typeof raw === 'string') {
        const m = raw.trim().match(/^(\d{4})\D+(\d{1,2})/);
        if (m) {
            return `${m[1]}-${String(Number(m[2])).padStart(2, '0')}`;
        }
    }
    const d = dayjs.isDayjs?.(raw) ? raw : dayjs(raw);
    return d.isValid() ? d.format('YYYY-MM') : null;
};

const monthLabel = computed(() => {
    const m = normalizeMonth(selectedMonth.value);
    if (!m) return '';
    return dayjs(`${m}-01`).format('YYYY年M月');
});

const monthPickerValue = computed(() => {
    const m = normalizeMonth(selectedMonth.value);
    return m ? dayjs(`${m}-01`) : dayjs().startOf('month');
});

const groupSlots = (flatSlots) => {
    const byDate = new Map();
    for (const raw of flatSlots) {
        if (!raw || typeof raw !== 'object') continue;
        const startsAt = raw.starts_at ?? raw.startsAt;
        if (!startsAt) continue;
        const d = taipeiDay(startsAt);
        if (!d.isValid()) continue;
        const hm = d.format('HH:mm');
        if (!SLOT_STARTS.includes(hm)) continue;
        const date = d.format('YYYY-MM-DD');
        const status = String(raw.status ?? '').toLowerCase();
        const isBookable = status === 'available' || Boolean(raw.is_current);
        const list = byDate.get(date) ?? [];
        list.push({
            startsAt: d.toISOString(),
            isBookable,
            label: formatSlotTimeLabel(startsAt),
            isCurrent: Boolean(raw.is_current)
        });
        byDate.set(date, list);
    }
    return [...byDate.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, slots]) => ({
            date,
            slots: slots.sort(
                (a, b) => taipeiDay(a.startsAt).valueOf() - taipeiDay(b.startsAt).valueOf()
            )
        }));
};

const syncMonthFromValue = (iso) => {
    if (!iso) return;
    const d = taipeiDay(iso);
    if (!d.isValid()) return;
    selectedDate.value = d.format('YYYY-MM-DD');
    const month = d.format('YYYY-MM');
    if (month !== selectedMonth.value) {
        selectedMonth.value = month;
    }
};

const loadSlots = async () => {
    if (!props.consultantCoreMemberId) {
        days.value = [];
        return;
    }

    const month = normalizeMonth(selectedMonth.value);
    if (!month) {
        error.value = '月份無效，請重新選擇';
        days.value = [];
        return;
    }
    if (month !== selectedMonth.value) {
        selectedMonth.value = month;
    }

    loading.value = true;
    error.value = '';
    try {
        // 時段依排班／容量，不依線上／實體分流（模式只寫入預約本身）
        const res = await getConsultationBookingAvailability({
            consultant_core_member_id: props.consultantCoreMemberId,
            mode: 'online',
            month,
            current_slot_starts_at: props.preserveSlotStartsAt || undefined
        });
        if (res.status !== 1) throw new Error(res.message || '載入時段失敗');
        const flat = res.data?.slots ?? [];
        days.value = groupSlots(flat);

        if (props.modelValue) {
            const d = taipeiDay(props.modelValue);
            if (d.isValid() && d.format('YYYY-MM') === month) {
                selectedDate.value = d.format('YYYY-MM-DD');
            } else if (days.value.length && !days.value.some((x) => x.date === selectedDate.value)) {
                selectedDate.value = days.value[0].date;
            }
        } else if (days.value.length && !selectedDate.value) {
            selectedDate.value = days.value[0].date;
        } else if (
            days.value.length &&
            selectedDate.value &&
            !days.value.some((x) => x.date === selectedDate.value)
        ) {
            selectedDate.value = days.value[0].date;
        }
    } catch (e) {
        error.value = e.message || '無法載入可預約時段';
        days.value = [];
    } finally {
        loading.value = false;
    }
};

const selectedDay = computed(() =>
    days.value.find((d) => d.date === selectedDate.value)
);

const selectSlot = (slot) => {
    if (!slot.isBookable || props.disabled) return;
    emit('update:modelValue', slot.startsAt);
};

const isSlotSelected = (slot) => {
    if (!props.modelValue || !slot.startsAt) return false;
    return taipeiDay(props.modelValue).format('YYYY-MM-DD HH:mm')
        === taipeiDay(slot.startsAt).format('YYYY-MM-DD HH:mm');
};

const onMonthChange = (date, dateString) => {
    const month = normalizeMonth(dateString) || normalizeMonth(date);
    if (!month) return;
    selectedMonth.value = month;
};

const refreshSlots = () => {
    void loadSlots();
};

/** 開啟編輯／換諮詢師時，對齊目前預約所在月份 */
watch(
    () => [props.reloadToken, props.consultantCoreMemberId, props.preserveSlotStartsAt],
    () => {
        const anchor = props.modelValue || props.preserveSlotStartsAt;
        if (!anchor) return;
        const d = taipeiDay(anchor);
        if (d.isValid()) {
            selectedMonth.value = d.format('YYYY-MM');
            selectedDate.value = d.format('YYYY-MM-DD');
        }
    },
    { immediate: true }
);

watch(
    () => [
        props.consultantCoreMemberId,
        selectedMonth.value,
        props.preserveSlotStartsAt,
        props.reloadToken
    ],
    () => {
        void loadSlots();
    },
    { immediate: true }
);

watch(
    () => props.modelValue,
    (iso) => {
        if (!iso) return;
        const d = taipeiDay(iso);
        if (d.isValid()) selectedDate.value = d.format('YYYY-MM-DD');
    }
);

defineExpose({ refreshSlots, loadSlots });
</script>

<template>
    <div class="consultation-slot-picker">
        <div class="consultation-slot-picker__toolbar">
            <DatePicker
                :value="monthPickerValue"
                picker="month"
                format="YYYY-MM"
                :allow-clear="false"
                :disabled="disabled"
                class="consultation-slot-picker__month-picker"
                @change="onMonthChange"
            />
            <Button
                size="small"
                type="text"
                class="consultation-slot-picker__reload"
                :disabled="disabled || !consultantCoreMemberId || loading"
                title="重新載入時段"
                @click="refreshSlots"
            >
                <template #icon><ReloadOutlined /></template>
            </Button>
        </div>

        <div v-if="loading" class="consultation-slot-picker__state">
            <Spin size="small" />
            <span>載入中…</span>
        </div>
        <p v-else-if="error" class="consultation-slot-picker__error">
            {{ error }}
            <Button type="link" size="small" class="!px-0" @click="refreshSlots">重試</Button>
        </p>
        <p v-else-if="!consultantCoreMemberId" class="consultation-slot-picker__hint">
            請先選擇諮詢師
        </p>
        <template v-else-if="days.length">
            <div class="consultation-slot-picker__dates" role="tablist" aria-label="日期">
                <Button
                    v-for="day in days"
                    :key="day.date"
                    size="small"
                    :type="selectedDate === day.date ? 'primary' : 'default'"
                    :disabled="disabled"
                    @click="selectedDate = day.date"
                >
                    {{ dayjs(day.date).format('M/D') }}
                </Button>
            </div>
            <div v-if="selectedDay" class="consultation-slot-picker__slots">
                <Button
                    v-for="slot in selectedDay.slots"
                    :key="slot.startsAt"
                    size="small"
                    :type="isSlotSelected(slot) ? 'primary' : 'default'"
                    :disabled="disabled || !slot.isBookable"
                    class="consultation-slot-picker__slot-btn"
                    @click="selectSlot(slot)"
                >
                    {{ slot.label
                    }}<span v-if="slot.isCurrent" class="consultation-slot-picker__current-tag"
                        >·目前</span
                    >
                </Button>
            </div>
            <p v-else class="consultation-slot-picker__hint">請選日期</p>
        </template>
        <p v-else class="consultation-slot-picker__hint">
            {{ monthLabel }}無可選時段
        </p>
    </div>
</template>

<style scoped>
.consultation-slot-picker {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.consultation-slot-picker__toolbar {
    display: flex;
    align-items: center;
    gap: 4px;
}

.consultation-slot-picker__month-picker {
    width: 128px;
}

.consultation-slot-picker__reload {
    color: #64748b;
}

.consultation-slot-picker__state {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #64748b;
}

.consultation-slot-picker__error {
    margin: 0;
    font-size: 12px;
    color: #dc2626;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.consultation-slot-picker__hint {
    margin: 0;
    font-size: 12px;
    color: #94a3b8;
    line-height: 1.4;
}

.consultation-slot-picker__dates,
.consultation-slot-picker__slots {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.consultation-slot-picker__slot-btn {
    height: auto;
    padding: 2px 10px;
    line-height: 1.4;
}

.consultation-slot-picker__current-tag {
    margin-left: 2px;
    font-size: 11px;
    opacity: 0.8;
}
</style>
